/* ==========================================================================
   CLUB LAPAROZ – Trip booking page controller
   <club-laparoz-trip> custom element used by sections/club-laparoz-trip.liquid

   • PDF downloads (blob download, no navigation, graceful fallbacks)
   • Responsive booking drawer (right panel on desktop, bottom sheet on mobile)
   • Booker form, ID proof selection, seats, dynamic traveller entries
   • Secure ID upload → booking server (private R2 storage, never public)
   • Razorpay order created + verified server-side; confirmation is shown only
     after the server confirms the payment signature and payment status.
   No secrets live in this file.
   ========================================================================== */
(() => {
  if (customElements.get('club-laparoz-trip')) return;

  const MAX_BYTES = 5 * 1024 * 1024;
  const OK_EXT = ['jpg', 'jpeg', 'png', 'pdf'];
  const OK_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
  const RZP_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
  const NAME_RE = /^[\p{L}][\p{L}\p{M} .'-]{1,79}$/u;
  const CITY_RE = /^[\p{L}][\p{L}\p{M} .'-]{1,59}$/u;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const CONFIG_ERR =
    'Secure ID storage and payments are not configured for this trip yet, so we could not take your booking. Please contact Club Laparoz to book your seat.';

  /* ---------------- helpers ---------------- */
  const esc = (v) =>
    String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fmtBytes = (b) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`);
  const money = (paise, cur = 'INR') => {
    const v = (Number(paise) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: cur, minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(v);
    } catch (e) {
      return `₹${v.toLocaleString('en-IN')}`;
    }
  };
  const absUrl = (u) => (u && u.startsWith('//') ? `https:${u}` : u);
  const titleCase = (s) => String(s || '').toLowerCase().replace(/(^|\s|&)\S/g, (m) => m.toUpperCase());
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  /** Wording for trips vs workshops. */
  const wordsFor = (kind) =>
    kind === 'workshop'
      ? { ws: true, unit: 'spot', units: 'spots', Person: 'Participant', People: 'Participants', what: 'workshop', What: 'Workshop', going: 'See you there!' }
      : { ws: false, unit: 'seat', units: 'seats', Person: 'Traveller', People: 'Travellers', what: 'trip', What: 'Trip', going: 'You are going!' };

  /** Advance amount in paise. Mirrors the server (worker/src/pricing.js). */
  function computeAdvance(totalPaise, seats, adv) {
    if (!adv || !adv.enabled) return 0;
    const value = Number(adv.value) || 0;
    let amt = adv.type === 'fixed' ? Math.round(value * 100) * seats : Math.round((totalPaise * value) / 100);
    if (amt < 100 || amt >= totalPaise) return 0;
    return amt;
  }

  let scrollLocks = 0;
  let savedY = 0;
  function lockScroll() {
    if (scrollLocks++ > 0) return;
    savedY = window.scrollY || 0;
    const b = document.body.style;
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    Object.assign(b, { position: 'fixed', top: `-${savedY}px`, left: '0', right: '0', width: '100%', paddingRight: sbw > 0 ? `${sbw}px` : '' });
    document.documentElement.classList.add('clpt-locked');
  }
  function unlockScroll() {
    if (scrollLocks === 0 || --scrollLocks > 0) return;
    const b = document.body.style;
    Object.assign(b, { position: '', top: '', left: '', right: '', width: '', paddingRight: '' });
    document.documentElement.classList.remove('clpt-locked');
    window.scrollTo({ top: savedY, left: 0, behavior: 'instant' });
  }

  let rzpPromise = null;
  function loadRazorpay() {
    if (window.Razorpay) return Promise.resolve();
    if (rzpPromise) return rzpPromise;
    rzpPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = RZP_SRC;
      s.async = true;
      s.onload = () => (window.Razorpay ? resolve() : reject(new Error('Razorpay failed to load')));
      s.onerror = () => {
        rzpPromise = null;
        reject(new Error('Could not load the Razorpay checkout. Please check your connection and try again.'));
      };
      document.head.appendChild(s);
    });
    return rzpPromise;
  }

  async function sniffFile(file) {
    const buf = new Uint8Array(await file.slice(0, 8).arrayBuffer());
    if (buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff) return 'image/jpeg';
    if (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) return 'image/png';
    if (buf[0] === 0x25 && buf[1] === 0x50 && buf[2] === 0x44 && buf[3] === 0x46) return 'application/pdf';
    return null;
  }

  /* ---------------------- Booking PDF (no libraries) ---------------------- */
  // Helvetica / Helvetica-Bold advance widths for chars 32..126 (per 1000 em).
  const HW = {
    r: [278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,1015,667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,667,778,722,667,611,722,667,944,667,667,611,278,278,278,469,556,333,556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,556,556,333,500,278,556,500,722,500,500,500,334,260,334,584],
    b: [278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,975,722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,667,778,722,667,611,722,667,944,667,667,611,333,278,333,584,556,333,556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,611,611,389,556,333,611,556,778,556,556,500,389,280,389,584],
  };
  const WIN = { 0x2013: 0x96, 0x2014: 0x97, 0x2022: 0x95, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93, 0x201d: 0x94, 0x2026: 0x85, 0x20ac: 0x80, 0xd7: 0xd7 };
  /** Text → WinAnsi single-byte string (₹ becomes "INR "). */
  function pdfText(str) {
    let out = '';
    for (const ch of String(str == null ? '' : str).replace(/₹\s?/g, 'INR ')) {
      const c = ch.codePointAt(0);
      if (c >= 32 && c <= 126) out += ch;
      else if (WIN[c]) out += String.fromCharCode(WIN[c]);
      else if (c >= 0xa0 && c <= 0xff) out += ch;
      else out += '?';
    }
    return out;
  }
  function pdfWidth(s, size, bold) {
    const t = bold ? HW.b : HW.r;
    let w = 0;
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      w += c >= 32 && c <= 126 ? t[c - 32] : 556;
    }
    return (w * size) / 1000;
  }
  function pdfWrap(s, size, bold, max) {
    const words = s.split(' ');
    const lines = [];
    let line = '';
    for (const w of words) {
      const tryLine = line ? `${line} ${w}` : w;
      if (pdfWidth(tryLine, size, bold) <= max || !line) line = tryLine;
      else {
        lines.push(line);
        line = w;
      }
    }
    if (line) lines.push(line);
    return lines;
  }
  const hexRgb = (hex, fb) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '')) || /^#?([0-9a-f]{6})$/i.exec(fb);
    const n = parseInt(m[1], 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255].map((v) => v.toFixed(3)).join(' ');
  };

  function buildBookingPdf(b, cfg) {
    const W = 595.28, H = 841.89, M = 48;
    const C = {
      brown: hexRgb(cfg.brown, '#251E19'),
      gold: hexRgb(cfg.gold, '#D9B16A'),
      deep: hexRgb(cfg.gold_deep, '#B38A50'),
      cream: hexRgb(cfg.cream, '#F6F1E7'),
      muted: '0.420 0.357 0.302',
      line: '0.910 0.875 0.820',
      white: '1 1 1',
    };
    const cur = b.currency;
    const pages = [];
    let ops = [];
    let y = 0;
    const esc = (t) => t.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
    const rect = (x, yy, w, h, color) => ops.push(`${color} rg ${x.toFixed(2)} ${yy.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re f`);
    const text = (t, x, yy, size, bold, color) => ops.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${color} rg ${x.toFixed(2)} ${yy.toFixed(2)} Td (${esc(t)}) Tj ET`);
    const textR = (t, xr, yy, size, bold, color) => text(t, xr - pdfWidth(t, size, bold), yy, size, bold, color);
    const spaced = (t, x, yy, size, bold, color, sp) => ops.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${sp} Tc ${color} rg ${x.toFixed(2)} ${yy.toFixed(2)} Td (${esc(t)}) Tj 0 Tc ET`);
    const hline = (yy) => ops.push(`${C.line} RG 0.7 w ${M} ${yy.toFixed(2)} m ${(W - M).toFixed(2)} ${yy.toFixed(2)} l S`);

    const newPage = (first) => {
      if (ops.length) pages.push(ops.join('\n'));
      ops = [];
      rect(0, 0, W, H, C.cream);
      if (first) {
        rect(0, H - 150, W, 150, C.brown);
        spaced(pdfText(cfg.brand || 'Club Laparoz').toUpperCase(), M, H - 52, 10, true, C.gold, 2.4);
        text('BOOKING CONFIRMED!', M, H - 92, 28, true, C.cream);
        const ref = pdfText(`Booking reference:  ${b.booking_ref}`);
        const rw = pdfWidth(ref, 11, true) + 28;
        rect(M, H - 134, rw, 26, C.gold);
        text(ref, M + 14, H - 125, 11, true, C.brown);
        const paid = pdfText(new Date(b.paid_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }));
        textR(pdfText('Paid on'), W - M, H - 112, 9, false, C.gold);
        textR(paid, W - M, H - 126, 10, true, C.cream);
        y = H - 186;
      } else {
        rect(0, H - 40, W, 40, C.brown);
        text(pdfText(`${cfg.brand || 'Club Laparoz'} · ${b.booking_ref} (continued)`), M, H - 25, 10, true, C.gold);
        y = H - 72;
      }
      rect(M - 16, 40, W - 2 * M + 32, y - 40 + 22, C.white);
    };
    const ensure = (h) => {
      if (y - h < 64) newPage(false);
    };
    const section = (title) => {
      ensure(60);
      spaced(pdfText(title.toUpperCase()), M, y, 9, true, C.deep, 1.6);
      y -= 17;
    };
    const row = (label, value, opts = {}) => {
      if (value == null || value === '') return;
      const size = opts.big ? 14 : 10.5;
      const lines = pdfWrap(pdfText(value), size, true, 300);
      ensure(lines.length * (size + 4) + 12);
      text(pdfText(label), M, y, opts.big ? 12 : 10, !!opts.big, opts.big ? C.brown : C.muted);
      lines.forEach((ln, i) => textR(ln, W - M, y - i * (size + 4), size, true, C.brown));
      y -= (lines.length - 1) * (size + 4);
      hline(y - 8);
      y -= opts.big ? 28 : 24;
    };

    const w = wordsFor(b.trip.kind || cfg.kind);
    newPage(true);
    section(w.What);
    row(w.What, b.trip.title);
    if (w.ws) {
      row('Date', b.trip.dates);
      row('Time', b.trip.duration);
      row('Venue', b.trip.venue || b.trip.destination);
    } else {
      row('Destination', b.trip.destination);
      row('Dates', b.trip.dates);
      row('Duration', b.trip.duration);
    }
    if (b.trip.variant && b.trip.variant !== 'Default Title') row('Package', b.trip.variant);
    y -= 6;
    section('Primary booker');
    row('Name', b.booker.name);
    row('Email', b.booker.email);
    row('Mobile', b.booker.phone);
    row('City', b.booker.city);
    y -= 6;
    section(`${w.People} (${b.seats})`);
    b.travellers.forEach((t, i) => row(`${i + 1}. ${t.name}`, `${t.age} yrs`));
    y -= 6;
    section('Payment');
    row('Price per person', money(b.price_per_person, cur));
    row(titleCase(w.units), String(b.seats));
    row(`Total ${w.what} amount`, money(b.total_amount, cur));
    row('Payment type', b.payment_mode === 'advance' ? 'Advance (pay the rest later)' : 'Full payment');
    if (b.balance_due > 0) row(`Balance due before the ${w.what}`, money(b.balance_due, cur));
    row('Razorpay payment ID', b.payment_id);
    row('Amount paid', money(b.amount_paid, cur), { big: true });
    ensure(70);
    y -= 6;
    const contact = [cfg.support_email, cfg.whatsapp ? `WhatsApp +${cfg.whatsapp}` : ''].filter(Boolean).join('  ·  ');
    const note = b.has_id === false || (b.has_id == null && w.ws) ? 'Show this booking reference at check-in.' : 'Please carry the original of the ID proof you uploaded. Show this booking reference at check-in.';
    pdfWrap(pdfText(note), 9, false, W - 2 * M).forEach((ln) => {
      text(ln, M, y, 9, false, C.muted);
      y -= 13;
    });
    if (contact) text(pdfText(`Questions? ${contact}`), M, y, 9, true, C.brown);
    pages.push(ops.join('\n'));

    // ---- assemble PDF objects ----
    const objs = [];
    const add = (body) => objs.push(body) && objs.length;
    const catalog = add(null);
    const pagesObj = add(null);
    const f1 = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
    const f2 = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
    const kids = [];
    pages.forEach((content, i) => {
      const footer = `BT /F1 8 Tf ${C.muted} rg ${M} 22 Td (${esc(pdfText(`${cfg.brand || 'Club Laparoz'} · ${b.booking_ref} · Page ${i + 1} of ${pages.length}`))}) Tj ET`;
      const stream = `${content}\n${footer}`;
      const cs = add(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
      kids.push(add(`<< /Type /Page /Parent ${pagesObj} 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /Font << /F1 ${f1} 0 R /F2 ${f2} 0 R >> >> /Contents ${cs} 0 R >>`));
    });
    objs[catalog - 1] = `<< /Type /Catalog /Pages ${pagesObj} 0 R >>`;
    objs[pagesObj - 1] = `<< /Type /Pages /Kids [${kids.map((k) => `${k} 0 R`).join(' ')}] /Count ${kids.length} >>`;
    let pdf = '%PDF-1.4\n%\xe2\xe3\xcf\xd3\n';
    const offsets = [];
    objs.forEach((body, i) => {
      offsets.push(pdf.length);
      pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
    });
    const xref = pdf.length;
    pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n${offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')}`;
    pdf += `trailer\n<< /Size ${objs.length + 1} /Root ${catalog} 0 R /Info << /Title (${esc(pdfText(`Booking ${b.booking_ref}`))}) /Producer (Club Laparoz) >> >>\nstartxref\n${xref}\n%%EOF`;
    const bytes = new Uint8Array(pdf.length);
    for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 255;
    return bytes;
  }

  class ApiError extends Error {
    constructor(message, code, status) {
      super(message);
      this.code = code;
      this.status = status;
    }
  }

  /* ======================================================================= */
  class ClubLaparozTrip extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.cfg = JSON.parse(this.querySelector('[data-clpt-config]').textContent);
      } catch (e) {
        console.error('[Club Laparoz] invalid trip config', e);
        this.cfg = { trip: null, display: {} };
      }
      this.trip = this.cfg.trip;
      this.w = wordsFor((this.trip && this.trip.kind) || this.cfg.kind);
      this.needId = ((this.trip && this.trip.require_id) ?? this.cfg.require_id) !== false;
      this.base = (this.dataset.apiBase || '').trim().replace(/\/+$/, '');
      this.state = { seats: 1, t1Touched: false, file: null, upload: null, payMode: 'full', step: 'form', booking: null, confirmed: null, busy: false };
      this.toastEl = this.querySelector('[data-clpt-toast]');
      this.bindDocs();
      this.bindVideo();
      this.layer = this.querySelector('[data-clpt-layer]');
      if (this.layer && this.trip) this.setupDrawer();
    }

    disconnectedCallback() {
      if (this.layer) {
        if (this.layer.classList.contains('is-open')) unlockScroll();
        this.layer.remove();
      }
      if (this._onHash) window.removeEventListener('hashchange', this._onHash);
      this._ready = false;
    }

    $(sel) {
      return this.layer.querySelector(sel);
    }
    $$(sel) {
      return Array.from(this.layer.querySelectorAll(sel));
    }

    toast(msg, ms = 3200) {
      const t = this.toastEl;
      if (!t) return;
      clearTimeout(this._toastT);
      t.textContent = msg;
      t.hidden = false;
      requestAnimationFrame(() => t.classList.add('is-visible'));
      this._toastT = setTimeout(() => {
        t.classList.remove('is-visible');
        setTimeout(() => (t.hidden = true), 400);
      }, ms);
    }

    /* ------------------------- PDF downloads ------------------------- */
    bindDocs() {
      this.querySelectorAll('[data-clpt-doc]').forEach((btn) => btn.addEventListener('click', () => this.downloadDoc(btn)));
    }

    async downloadDoc(btn) {
      const url = absUrl(btn.dataset.url || '');
      const label = titleCase(btn.dataset.label || 'Document');
      const name = btn.dataset.filename || 'club-laparoz-document.pdf';
      if (!url) {
        this.toast(`The ${label} PDF for this trip isn't available yet. Please check back soon.`);
        return;
      }
      if (btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        let blob = null;
        let notFound = false;
        const attempts = [url];
        if (this.base) attempts.push(`${this.base}/api/document?url=${encodeURIComponent(url)}`);
        for (const [i, u] of attempts.entries()) {
          try {
            const res = await fetch(u, { mode: 'cors', credentials: 'omit' });
            // 404 means the file is gone. A 403 from the proxy only means "host not allowed".
            if (res.status === 404 || (i === 0 && res.status === 403)) {
              notFound = true;
              break;
            }
            if (!res.ok) continue;
            const b = await res.blob();
            if (b.size > 0) {
              blob = b;
              break;
            }
          } catch (e) {
            /* CORS / network – try next strategy */
          }
        }
        if (notFound) {
          this.toast(`Sorry, the ${label} PDF couldn't be found. Please contact us and we'll send it to you.`, 4500);
          return;
        }
        if (blob) {
          const pdf = blob.type && blob.type.includes('pdf') ? blob : new Blob([blob], { type: 'application/pdf' });
          const href = URL.createObjectURL(pdf);
          const a = document.createElement('a');
          a.href = href;
          a.download = name;
          a.rel = 'noopener';
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(href), 4000);
          this.toast(`${label} PDF downloaded`);
        } else {
          // Last resort: open in a new tab so this page never navigates away.
          // ('noopener' would make window.open return null, hiding pop-up blocking.)
          const w = window.open(url, '_blank');
          if (w) w.opener = null;
          this.toast(w ? `The ${label} PDF opened in a new tab.` : `Please allow pop-ups to open the ${label} PDF.`, 4000);
        }
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }

    /* ----------------------------- Video ----------------------------- */
    bindVideo() {
      const btn = this.querySelector('[data-clpt-video]');
      const modal = this.querySelector('[data-clpt-video-modal]');
      if (!btn || !modal) return;
      const frame = modal.querySelector('[data-clpt-video-frame]');
      const close = () => {
        modal.hidden = true;
        frame.innerHTML = '';
        unlockScroll();
        btn.focus({ preventScroll: true });
        document.removeEventListener('keydown', onKey);
      };
      const onKey = (e) => e.key === 'Escape' && close();
      modal.querySelectorAll('[data-clpt-video-close]').forEach((b) => b.addEventListener('click', close));
      btn.addEventListener('click', () => {
        const src = btn.dataset.clptVideo;
        const embed = this.videoEmbed(src);
        if (!embed) {
          window.open(src, '_blank', 'noopener');
          return;
        }
        frame.innerHTML = embed;
        modal.hidden = false;
        lockScroll();
        document.addEventListener('keydown', onKey);
        modal.querySelector('.clpt-video__close').focus({ preventScroll: true });
      });
    }

    videoEmbed(src) {
      let u;
      try {
        u = new URL(absUrl(src), location.href);
      } catch (e) {
        return null;
      }
      const host = u.hostname.replace(/^www\.|^m\./, '');
      let id = null;
      if (host === 'youtu.be') id = u.pathname.slice(1);
      else if (host.endsWith('youtube.com')) id = u.searchParams.get('v') || (u.pathname.match(/\/(?:embed|shorts|live)\/([^/?]+)/) || [])[1];
      if (id) return `<iframe src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1" title="Trip video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>`;
      if (host.endsWith('vimeo.com')) {
        const vid = (u.pathname.match(/(\d+)/) || [])[1];
        if (vid) return `<iframe src="https://player.vimeo.com/video/${vid}?autoplay=1" title="Trip video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
      }
      if (/\.(mp4|webm|mov|m4v)$/i.test(u.pathname)) return `<video src="${esc(u.href)}" controls autoplay playsinline></video>`;
      return null;
    }

    /* ----------------------------- Drawer ----------------------------- */
    setupDrawer() {
      document.body.appendChild(this.layer);
      this.drawer = this.$('.clpt-drawer');
      this.form = this.$('[data-clpt-step="form"]');
      this.f = (n) => this.form.elements.namedItem(n);
      this.travellersEl = this.$('[data-clpt-travellers]');
      this.uid = this.id;

      this.querySelectorAll('[data-clpt-open]').forEach((b) => b.addEventListener('click', () => this.open(b)));
      this.$$('[data-clpt-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.layer.addEventListener('keydown', (e) => this.onKey(e));
      this.$$('[data-clpt-doc-link]').forEach((a) =>
        a.addEventListener('click', (e) => {
          e.preventDefault();
          const btn = this.querySelector(`[data-clpt-doc="${a.dataset.clptDocLink}"]`);
          if (btn) this.downloadDoc(btn);
        })
      );

      // Variants (packages)
      const variants = (this.trip.variants || []).filter((v) => v.available);
      this.variant = variants.find((v) => v.id === this.trip.selected_variant_id) || variants[0] || this.trip.variants[0];
      if (variants.length > 1) {
        const sel = this.$('[data-clpt-variant]');
        sel.innerHTML = variants.map((v) => `<option value="${v.id}">${esc(v.title)} — ${esc(money(v.price, this.trip.currency))}</option>`).join('');
        sel.value = String(this.variant.id);
        sel.addEventListener('change', () => {
          this.variant = variants.find((v) => String(v.id) === sel.value);
          this.setSeats(this.state.seats);
          this.state.upload = this.state.upload; // ID upload is independent of package
        });
        this.$('[data-clpt-variant-wrap]').hidden = false;
      }

      // Booker name → Traveller 1
      this.f('name').addEventListener('input', () => {
        const t1 = this.travellersEl.querySelector('[name="t_name"]');
        if (t1 && !this.state.t1Touched) t1.value = this.f('name').value;
      });
      this.f('phone').addEventListener('input', (e) => {
        const v = e.target.value.replace(/[^\d ]/g, '');
        if (v !== e.target.value) e.target.value = v;
      });
      // The ID proof input manages its own error state in selectFile().
      this.fileInput = this.needId ? this.f('id_proof') : null;
      if (this.needId && !this.fileInput) this.needId = false; // markup without the ID field
      this.form.addEventListener('input', (e) => e.target !== this.fileInput && this.clearFieldError(e.target));
      this.form.addEventListener('change', (e) => e.target !== this.fileInput && this.clearFieldError(e.target));
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.proceed();
      });

      // File
      if (this.fileInput) this.bindFile();

      this.bindSeatsAndActions();
    }

    bindFile() {
      this.fileInput.addEventListener('change', () => {
        const file = this.fileInput.files && this.fileInput.files[0];
        if (file) this.selectFile(file);
      });
      this.$('[data-clpt-file-remove]').addEventListener('click', () => this.removeFile());
      const fileBox = this.$('[data-clpt-file]');
      ['dragenter', 'dragover'].forEach((t) => fileBox.addEventListener(t, (e) => { e.preventDefault(); fileBox.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((t) => fileBox.addEventListener(t, (e) => { e.preventDefault(); fileBox.classList.remove('is-drag'); }));
      fileBox.addEventListener('drop', (e) => {
        const file = e.dataTransfer && e.dataTransfer.files[0];
        if (file) this.selectFile(file);
      });
    }

    bindSeatsAndActions() {
      this.$$('[data-clpt-seat]').forEach((b) => b.addEventListener('click', () => this.setSeats(this.state.seats + Number(b.dataset.clptSeat))));
      const ages = this.$('[data-clpt-age-note]');
      ages.textContent = this.w.ws
        ? `Enter the full name and age of everyone attending. ${this.w.People} must be ${this.trip.min_age}–${this.trip.max_age} years old.`
        : `Enter the full name and age of everyone travelling. Travellers must be ${this.trip.min_age}–${this.trip.max_age} years old.`;
      this.setSeats(1);

      // Steps & actions
      this.$('[data-clpt-proceed]').addEventListener('click', () => this.proceed());
      this.$('[data-clpt-pay]').addEventListener('click', () => this.pay());
      this.$('[data-clpt-back]').addEventListener('click', () => this.setStep('form'));
      this.$('[data-clpt-download]').addEventListener('click', () => this.downloadBooking());
      this.$('[data-clpt-copy-ref]').addEventListener('click', () => {
        const ref = this.state.confirmed && this.state.confirmed.booking_ref;
        if (ref && navigator.clipboard) navigator.clipboard.writeText(ref).then(() => this.toast('Booking reference copied'));
      });
      this.$$('input[name^="clpt-paymode"]').forEach((r) =>
        r.addEventListener('change', () => {
          this.state.payMode = r.value;
          this.updatePayable();
        })
      );

      this._onHash = () => location.hash === '#book' && this.open();
      window.addEventListener('hashchange', this._onHash);
      if (location.hash === '#book') setTimeout(() => this.open(), 300);
    }

    open(trigger) {
      if (this.layer.classList.contains('is-open')) return;
      this.lastFocus = trigger || document.activeElement;
      this.layer.classList.add('is-open');
      lockScroll();
      setTimeout(() => {
        const first = this.state.step === 'form' ? this.f('name') : this.drawer;
        (first || this.drawer).focus({ preventScroll: true });
      }, 80);
    }

    close() {
      if (!this.layer.classList.contains('is-open')) return;
      if (this.state.busy) {
        this.toast('Please wait while we finish processing your booking.');
        return;
      }
      this.layer.classList.remove('is-open');
      unlockScroll();
      if (location.hash === '#book') history.replaceState(null, '', location.pathname + location.search);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = Array.from(
        this.drawer.querySelectorAll('button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((n) => n.offsetParent !== null || n.classList.contains('clpt-file__input') || n.type === 'checkbox' || n.type === 'radio');
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.drawer)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    setStep(step) {
      this.state.step = step;
      this.$$('[data-clpt-step]').forEach((el) => (el.hidden = el.dataset.clptStep !== step));
      this.$$('[data-clpt-foot]').forEach((el) => (el.hidden = el.dataset.clptFoot !== step));
      const order = ['form', 'summary', 'confirm'];
      this.$$('[data-clpt-progress]').forEach((li) => {
        const i = order.indexOf(li.dataset.clptProgress);
        const cur = order.indexOf(step);
        li.classList.toggle('is-active', i === cur);
        li.classList.toggle('is-done', i < cur);
        if (i === cur) li.setAttribute('aria-current', 'step');
        else li.removeAttribute('aria-current');
      });
      this.$('[data-clpt-step-label]').textContent = { form: `Book your ${this.w.unit}`, summary: 'Review & pay', confirm: this.w.going }[step];
      this.$('[data-clpt-scroll]').scrollTop = 0;
      const focusEl = step === 'confirm' ? this.$('[data-clpt-step="confirm"]') : step === 'summary' ? this.$('[data-clpt-pay]') : this.f('name');
      setTimeout(() => focusEl && focusEl.focus({ preventScroll: true }), 50);
    }

    /* ------------------------ seats & travellers ------------------------ */
    maxSeats() {
      const left = this.variant && this.variant.seats_left;
      const cap = Number(this.trip.max_seats) || 1;
      return typeof left === 'number' ? Math.max(0, Math.min(cap, left)) : cap;
    }

    setSeats(n) {
      const max = this.maxSeats();
      const seats = Math.max(1, Math.min(max || 1, n));
      this.state.seats = seats;
      this.$('[data-clpt-seat-value]').textContent = seats;
      this.$('[data-clpt-seat="-1"]').disabled = seats <= 1;
      this.$('[data-clpt-seat="1"]').disabled = seats >= max;
      this.$('[data-clpt-ppp]').textContent = money(this.variant.price, this.trip.currency);
      const left = this.variant.seats_left;
      const note = this.$('[data-clpt-seat-note]');
      if (max === 0) note.textContent = 'This package is sold out.';
      else if (typeof left === 'number' && left <= max) note.textContent = `Only ${left} ${left === 1 ? this.w.unit : this.w.units} left. Maximum ${max} per booking.`;
      else note.textContent = `Maximum ${max} ${max === 1 ? this.w.unit : this.w.units} per booking.`;
      this.syncTravellers();
    }

    syncTravellers() {
      const box = this.travellersEl;
      const n = this.state.seats;
      while (box.children.length < n) box.appendChild(this.travellerRow(box.children.length));
      while (box.children.length > n) box.lastElementChild.remove();
    }

    travellerRow(i) {
      const id = `${this.uid}-t${i}-${Date.now().toString(36)}`;
      const row = document.createElement('div');
      row.className = 'clpt-traveller';
      row.dataset.index = i;
      const { min_age: min, max_age: max } = this.trip;
      row.innerHTML = `
        <div class="clpt-traveller__head">
          <span class="clpt-traveller__num">${i + 1}</span>${this.w.Person} ${i + 1}
          ${i === 0 ? '<span class="clpt-traveller__tag">Primary booker</span>' : ''}
        </div>
        <div class="clpt-traveller__grid">
          <div class="clpt-field">
            <label class="clpt-label" for="${id}-n">Full name <span aria-hidden="true">*</span></label>
            <input class="clpt-input" id="${id}-n" name="t_name" type="text" maxlength="80" autocomplete="${i === 0 ? 'name' : 'off'}" aria-describedby="${id}-ne">
            <p class="clpt-err" id="${id}-ne"></p>
          </div>
          <div class="clpt-field">
            <label class="clpt-label" for="${id}-a">Age <span aria-hidden="true">*</span></label>
            <input class="clpt-input" id="${id}-a" name="t_age" type="number" inputmode="numeric" min="${min}" max="${max}" step="1" aria-describedby="${id}-ae">
            <p class="clpt-err" id="${id}-ae"></p>
          </div>
        </div>`;
      if (i === 0) {
        const nameIn = row.querySelector('[name="t_name"]');
        nameIn.value = this.f('name').value;
        nameIn.addEventListener('input', () => {
          this.state.t1Touched = nameIn.value.trim() !== this.f('name').value.trim();
        });
      }
      return row;
    }

    /* ----------------------------- ID proof ----------------------------- */
    async selectFile(file) {
      const err = this.validateFileBasic(file) || (await this.validateFileContent(file));
      this.fileInput.value = '';
      if (err) {
        this.setError(this.fileInput, err);
        this.$('[data-clpt-file]').classList.add('is-invalid');
        return;
      }
      this.state.file = file;
      this.state.upload = null;
      this.clearFieldError(this.fileInput);
      this.$('[data-clpt-file]').classList.remove('is-invalid');
      this.$('[data-clpt-file-name]').textContent = file.name;
      this.$('[data-clpt-file-size]').textContent = fmtBytes(file.size);
      this.$('[data-clpt-file-empty]').hidden = true;
      this.$('[data-clpt-file-chosen]').hidden = false;
    }

    removeFile() {
      this.state.file = null;
      this.state.upload = null;
      this.fileInput.value = '';
      this.$('[data-clpt-file-empty]').hidden = false;
      this.$('[data-clpt-file-chosen]').hidden = true;
      this.fileInput.focus({ preventScroll: true });
    }

    validateFileBasic(file) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!OK_EXT.includes(ext) || (file.type && !OK_MIME.includes(file.type))) return 'Please upload a JPG, JPEG, PNG or PDF file.';
      if (file.size === 0) return 'This file is empty. Please choose another file.';
      if (file.size > MAX_BYTES) return `This file is ${fmtBytes(file.size)}. The maximum size is 5 MB.`;
      return '';
    }

    async validateFileContent(file) {
      try {
        const kind = await sniffFile(file);
        if (!kind) return "This file doesn't look like a valid JPG, PNG or PDF. Please choose another file.";
      } catch (e) {
        return 'We could not read this file. Please choose another file.';
      }
      return '';
    }

    /* ---------------------------- validation ---------------------------- */
    errEl(input) {
      const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/);
      for (const id of ids) {
        const el = id && document.getElementById(id);
        if (el && el.classList.contains('clpt-err')) return el;
      }
      return null;
    }

    setError(input, msg) {
      const el = this.errEl(input);
      if (el) el.textContent = msg;
      input.setAttribute('aria-invalid', 'true');
    }

    clearFieldError(input) {
      if (!input || !input.getAttribute || input.getAttribute('aria-invalid') !== 'true') return;
      input.removeAttribute('aria-invalid');
      const el = this.errEl(input);
      if (el) el.textContent = '';
      if (input === this.fileInput) this.$('[data-clpt-file]').classList.remove('is-invalid');
      if (input.name === 'phone_code') this.clearFieldError(this.f('phone'));
    }

    collect() {
      const v = (n) => (this.f(n).value || '').trim().replace(/\s+/g, ' ');
      return {
        name: v('name'),
        email: v('email').toLowerCase(),
        phone_code: v('phone_code'),
        phone: v('phone').replace(/\D/g, ''),
        city: v('city'),
        travellers: Array.from(this.travellersEl.children).map((row) => ({
          name: row.querySelector('[name="t_name"]').value.trim().replace(/\s+/g, ' '),
          age: row.querySelector('[name="t_age"]').value.trim(),
        })),
      };
    }

    validate() {
      const d = this.collect();
      const bad = [];
      const check = (input, ok, msg) => {
        if (ok) return;
        this.setError(input, msg);
        bad.push(input);
      };
      check(this.f('name'), NAME_RE.test(d.name), d.name ? 'Please enter a valid full name (letters only).' : 'Please enter your full name.');
      check(this.f('email'), EMAIL_RE.test(d.email), d.email ? 'Please enter a valid email address.' : 'Please enter your email address.');
      const codeOk = /^\+\d{1,4}$/.test(d.phone_code);
      const phoneOk = d.phone_code === '+91' ? /^[6-9]\d{9}$/.test(d.phone) : /^\d{6,14}$/.test(d.phone);
      if (!codeOk) {
        this.setError(this.f('phone_code'), '');
        check(this.f('phone'), false, 'Please enter a valid country code, e.g. +91.');
      } else {
        check(
          this.f('phone'),
          phoneOk,
          !d.phone ? 'Please enter your mobile number.' : d.phone_code === '+91' ? 'Please enter a valid 10-digit Indian mobile number.' : 'Please enter a valid mobile number.'
        );
      }
      check(this.f('city'), CITY_RE.test(d.city), d.city ? 'Please enter a valid city name.' : 'Please enter your city.');
      if (this.needId) {
        check(this.fileInput, !!this.state.file, 'Please upload your ID proof.');
        if (!this.state.file) this.$('[data-clpt-file]').classList.add('is-invalid');
      }

      const { min_age: min, max_age: max } = this.trip;
      Array.from(this.travellersEl.children).forEach((row, i) => {
        const t = d.travellers[i];
        const nIn = row.querySelector('[name="t_name"]');
        const aIn = row.querySelector('[name="t_age"]');
        check(nIn, NAME_RE.test(t.name), t.name ? 'Please enter a valid name.' : `Please enter ${this.w.Person} ${i + 1}'s full name.`);
        const age = Number(t.age);
        check(aIn, t.age !== '' && Number.isInteger(age) && age >= min && age <= max, t.age === '' ? 'Enter age.' : `Age must be ${min}–${max}.`);
      });
      if (d.travellers.length !== this.state.seats) bad.push(this.travellersEl);
      check(this.f('consent_terms'), this.f('consent_terms').checked, 'Please accept the Terms & Conditions and Cancellation Policy.');
      if (this.needId) check(this.f('consent_id'), this.f('consent_id').checked, 'Please consent to the processing of your ID proof.');
      if (this.maxSeats() === 0) {
        this.formAlert('Sorry, this package is sold out.');
        return null;
      }
      if (bad.length) {
        const first = bad[0];
        this.formAlert(`Please complete ${bad.length === 1 ? 'the highlighted field' : `the ${bad.length} highlighted fields`} to continue.`);
        const target = this.fileInput && first === this.fileInput ? this.$('[data-clpt-file]') : first.closest('.clpt-field') || first;
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(() => first.focus && first.focus({ preventScroll: true }), 250);
        return null;
      }
      d.travellers = d.travellers.map((t) => ({ name: t.name, age: Number(t.age) }));
      return d;
    }

    formAlert(msg) {
      const a = this.$('[data-clpt-form-alert]');
      a.textContent = msg || '';
      a.hidden = !msg;
    }

    payAlert(msg) {
      const a = this.$('[data-clpt-pay-alert]');
      a.textContent = msg || '';
      a.hidden = !msg;
      if (msg) this.$('[data-clpt-scroll]').scrollTop = 0;
    }

    /* ------------------------------ network ------------------------------ */
    async api(path, body, { retries = 0 } = {}) {
      for (let attempt = 0; ; attempt++) {
        try {
          const res = await fetch(this.base + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
            credentials: 'omit',
          });
          let data = null;
          try {
            data = await res.json();
          } catch (e) {
            /* non-JSON */
          }
          if (!res.ok || !data || data.ok === false) {
            const err = (data && data.error) || {};
            throw new ApiError(err.message || `Something went wrong (${res.status}). Please try again.`, err.code || 'http_error', res.status);
          }
          return data;
        } catch (e) {
          const network = !(e instanceof ApiError);
          if (network && attempt < retries) {
            await sleep(800 * (attempt + 1));
            continue;
          }
          if (network) throw new ApiError('We could not reach the booking server. Please check your connection and try again.', 'network', 0);
          throw e;
        }
      }
    }

    uploadId(file, onProgress) {
      return new Promise((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', file, file.name);
        fd.append('handle', this.trip.handle);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${this.base}/api/id-proof`);
        xhr.responseType = 'json';
        xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
        xhr.onload = () => {
          const data = xhr.response || {};
          if (xhr.status >= 200 && xhr.status < 300 && data.ok && data.upload_token) resolve(data);
          else {
            const err = data.error || {};
            reject(new ApiError(err.message || `ID upload failed (${xhr.status}). Please try again.`, err.code || 'upload_failed', xhr.status));
          }
        };
        xhr.onerror = () => reject(new ApiError('We could not upload your ID proof. Please check your connection and try again.', 'network', 0));
        xhr.send(fd);
      });
    }

    setBusy(btn, busy, label) {
      this.state.busy = busy;
      btn.classList.toggle('is-loading', busy);
      btn.disabled = busy;
      btn.setAttribute('aria-busy', String(busy));
      if (label != null) btn.querySelector('.clpt-btn__label').textContent = label;
    }

    /* ---------------------------- step 1 → 2 ---------------------------- */
    async proceed() {
      if (this.state.busy) return;
      this.formAlert('');
      const data = this.validate();
      if (!data) return;
      if (!this.base) {
        this.formAlert(CONFIG_ERR);
        this.$('[data-clpt-scroll]').scrollTop = 0;
        return;
      }
      const btn = this.$('[data-clpt-proceed]');
      try {
        if (this.needId && (!this.state.upload || this.state.upload.file !== this.state.file)) {
          this.setBusy(btn, true, 'UPLOADING ID…');
          const res = await this.uploadId(this.state.file, (p) => (btn.querySelector('.clpt-btn__label').textContent = `UPLOADING ID… ${p}%`));
          this.state.upload = { file: this.state.file, token: res.upload_token };
        }
        this.data = data;
        this.renderSummary();
        this.setStep('summary');
      } catch (e) {
        this.formAlert(e.code === 'storage_not_configured' ? CONFIG_ERR : e.message);
        if (e.code === 'invalid_file' && this.fileInput) this.setError(this.fileInput, e.message);
        this.$('[data-clpt-scroll]').scrollTop = 0;
      } finally {
        this.setBusy(btn, false, 'PROCEED TO PAYMENT');
      }
    }

    amounts(serverOverride) {
      const seats = this.state.seats;
      const ppp = this.variant.price;
      const total = ppp * seats;
      const advance = computeAdvance(total, seats, this.trip.advance);
      const mode = advance > 0 && this.state.payMode === 'advance' ? 'advance' : 'full';
      const payable = serverOverride != null ? serverOverride : mode === 'advance' ? advance : total;
      return { seats, ppp, total, advance, mode, payable, balance: total - payable };
    }

    renderSummary() {
      const d = this.data;
      const disp = this.cfg.display || {};
      const cur = this.trip.currency;
      const a = this.amounts();
      const variantLine = (this.trip.variants || []).length > 1 ? ` · ${esc(this.variant.title)}` : '';
      this.$('[data-clpt-summary]').innerHTML = `
        <section class="clpt-card">
          <h3 class="clpt-card__title">${this.w.What}</h3>
          <div class="clpt-trip">
            ${disp.image ? `<img src="${esc(absUrl(disp.image))}" alt="" width="72" height="72" loading="lazy">` : ''}
            <div><strong>${esc(disp.title)}</strong>
              <span>${esc([this.w.ws ? disp.venue || disp.destination : disp.destination, disp.dates].filter(Boolean).join(' · '))}</span>
              <span>${esc(disp.duration || '')}${variantLine}</span></div>
          </div>
        </section>
        <section class="clpt-card">
          <h3 class="clpt-card__title">Primary booker</h3>
          <dl class="clpt-rows">
            <div><dt>Name</dt><dd>${esc(d.name)}</dd></div>
            <div><dt>Email</dt><dd>${esc(d.email)}</dd></div>
            <div><dt>Mobile</dt><dd>${esc(`${d.phone_code} ${d.phone}`)}</dd></div>
            <div><dt>City</dt><dd>${esc(d.city)}</dd></div>
            ${this.needId && this.state.file ? `<div><dt>ID proof</dt><dd>${esc(this.state.file.name)} ✓</dd></div>` : ''}
          </dl>
        </section>
        <section class="clpt-card">
          <h3 class="clpt-card__title">${this.w.People} (${a.seats})</h3>
          <ol class="clpt-tlist">${d.travellers.map((t, i) => `<li><span>${i + 1}. ${esc(t.name)}</span><span>${t.age} yrs</span></li>`).join('')}</ol>
        </section>
        <section class="clpt-card">
          <h3 class="clpt-card__title">Price</h3>
          <dl class="clpt-rows">
            <div><dt>Price per person</dt><dd>${money(a.ppp, cur)}</dd></div>
            <div><dt>Number of ${this.w.units}</dt><dd>× ${a.seats}</dd></div>
            <div class="is-total"><dt>Total amount</dt><dd>${money(a.total, cur)}</dd></div>
          </dl>
        </section>`;
      const modeWrap = this.$('[data-clpt-paymode-wrap]');
      modeWrap.hidden = !(a.advance > 0);
      if (a.advance > 0) {
        const adv = this.trip.advance || {};
        const pct = adv.type === 'percent' ? `${Number(adv.value)}% ` : '';
        this.$('input[name^="clpt-paymode"][value="full"] + .clpt-paymode__card strong').textContent = 'Pay in full';
        this.$('input[name^="clpt-paymode"][value="advance"] + .clpt-paymode__card strong').textContent = `Pay ${pct}advance`;
        this.$('[data-clpt-full-amt]').textContent = `${money(a.total, cur)} now, nothing later`;
        this.$('[data-clpt-adv-amt]').textContent = `${money(a.advance, cur)} now to book your ${this.w.unit}. Pay the rest later.`;
      } else {
        this.state.payMode = 'full';
        const full = this.$('input[name^="clpt-paymode"][value="full"]');
        if (full) full.checked = true;
      }
      this.payAlert('');
      this.updatePayable();
    }

    updatePayable() {
      const a = this.amounts();
      const cur = this.trip.currency;
      this.$('[data-clpt-payable]').textContent = money(a.payable, cur);
      // Set the whole label: setBusy() replaces label text, so never rely on inner spans.
      this.$('[data-clpt-pay] .clpt-btn__label').textContent = `PAY ${money(a.payable, cur)}`;
      this.$('[data-clpt-balance-note]').textContent =
        a.mode === 'advance' ? `Your ${this.w.unit} is booked with ${money(a.payable, cur)} today. Pay the remaining ${money(a.balance, cur)} later, before the ${this.w.what}.` : '';
    }

    /* ------------------------------ payment ------------------------------ */
    async pay() {
      if (this.state.busy) return;
      this.payAlert('');
      const btn = this.$('[data-clpt-pay]');
      const a = this.amounts();
      const d = this.data;
      const payLabel = () => `PAY ${money(this.amounts().payable, this.trip.currency)}`;
      this.setBusy(btn, true, 'CREATING SECURE ORDER…');
      let order;
      try {
        order = await this.api('/api/bookings', {
          handle: this.trip.handle,
          variant_id: this.variant.id,
          seats: a.seats,
          payment_mode: a.mode,
          booker: { name: d.name, email: d.email, phone: `${d.phone_code}${d.phone}`, city: d.city },
          travellers: d.travellers,
          consents: this.needId ? { terms: true, id_proof: true } : { terms: true },
          upload_token: this.needId && this.state.upload ? this.state.upload.token : undefined,
          display: {
            title: (this.cfg.display && this.cfg.display.title) || '',
            venue: (this.cfg.display && this.cfg.display.venue) || '',
            destination: (this.cfg.display && this.cfg.display.destination) || '',
            dates: (this.cfg.display && this.cfg.display.dates) || '',
            duration: (this.cfg.display && this.cfg.display.duration) || '',
          },
        });
        await loadRazorpay();
      } catch (e) {
        if (e.code === 'upload_expired' || e.code === 'upload_missing') {
          this.state.upload = null;
          this.setBusy(btn, false, payLabel());
          this.setStep('form');
          this.formAlert('Your ID upload expired. Please press Proceed to Payment again to re-upload it securely.');
          return;
        }
        this.payAlert(e.code === 'storage_not_configured' || e.code === 'payments_not_configured' ? CONFIG_ERR : e.message);
        this.setBusy(btn, false, payLabel());
        return;
      }

      if (order.amount !== a.payable) {
        // Server is authoritative (e.g. price changed). Show the real amount.
        this.$('[data-clpt-payable]').textContent = money(order.amount, order.currency);
      }
      this.state.booking = order;
      this.setBusy(btn, true, 'COMPLETE PAYMENT IN RAZORPAY…');
      this.layer.classList.add('is-paying');

      const rzp = new window.Razorpay({
        key: order.razorpay.key_id,
        order_id: order.razorpay.order_id,
        amount: order.amount,
        currency: order.currency,
        name: this.cfg.brand || 'Club Laparoz',
        description: `${(this.cfg.display && this.cfg.display.title) || this.w.What} · ${a.seats} ${a.seats > 1 ? this.w.units : this.w.unit} · ${order.booking_ref}`,
        prefill: { name: d.name, email: d.email, contact: `${d.phone_code}${d.phone}` },
        notes: { booking_ref: order.booking_ref },
        theme: { color: this.cfg.gold || '#D9B16A' },
        retry: { enabled: true },
        modal: {
          escape: true,
          confirm_close: true,
          ondismiss: () => {
            if (this._verifying) return;
            this.layer.classList.remove('is-paying');
            this.setBusy(btn, false, payLabel());
            this.payAlert(`Payment was not completed, so your ${this.w.unit} isn't booked yet. You can try again whenever you're ready.`);
          },
        },
        handler: (resp) => this.verify(order, resp, btn),
      });
      rzp.on('payment.failed', (r) => {
        const reason = r && r.error && (r.error.description || r.error.reason);
        this.payAlert(`Payment failed${reason ? `: ${reason}` : ''}. No booking was made. You can retry in the Razorpay window or choose another method.`);
      });
      rzp.open();
    }

    async verify(order, resp, btn) {
      this._verifying = true;
      this.layer.classList.remove('is-paying');
      this.setBusy(btn, true, 'VERIFYING PAYMENT…');
      try {
        const res = await this.api(
          '/api/bookings/verify',
          {
            booking_ref: order.booking_ref,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          },
          { retries: 3 }
        );
        if (!res.booking || res.booking.status !== 'confirmed') throw new ApiError('Payment could not be verified.', 'not_verified', 0);
        this.state.confirmed = res.booking;
        this.renderConfirmation(res.booking);
        this.setBusy(btn, false, 'PAID');
        this.setStep('confirm');
      } catch (e) {
        this.setBusy(btn, false, `PAY ${money(order.amount, order.currency)}`);
        this.payAlert(
          `We couldn't verify your payment yet (${e.message}) Your booking reference is ${order.booking_ref}. If money was debited, our system will confirm it automatically once Razorpay notifies us, or it will be refunded. Please contact us with this reference before paying again.`
        );
      } finally {
        this._verifying = false;
      }
    }

    /* ---------------------------- confirmation ---------------------------- */
    renderConfirmation(b) {
      const cur = b.currency;
      const w = wordsFor(b.trip.kind || (this.trip && this.trip.kind) || this.cfg.kind);
      this.$('[data-clpt-ref]').textContent = b.booking_ref;
      this.$('[data-clpt-confirm-email]').textContent = b.booker.email;
      this.$('[data-clpt-confirm-details]').innerHTML = `
        <section class="clpt-card">
          <h3 class="clpt-card__title">${w.What} details</h3>
          <dl class="clpt-rows">
            <div><dt>${w.What}</dt><dd>${esc(b.trip.title)}</dd></div>
            ${w.ws
              ? `${b.trip.dates ? `<div><dt>Date</dt><dd>${esc(b.trip.dates)}</dd></div>` : ''}
                 ${b.trip.duration ? `<div><dt>Time</dt><dd>${esc(b.trip.duration)}</dd></div>` : ''}
                 ${b.trip.venue || b.trip.destination ? `<div><dt>Venue</dt><dd>${esc(b.trip.venue || b.trip.destination)}</dd></div>` : ''}`
              : `${b.trip.destination ? `<div><dt>Destination</dt><dd>${esc(b.trip.destination)}</dd></div>` : ''}
                 ${b.trip.dates ? `<div><dt>Dates</dt><dd>${esc(b.trip.dates)}</dd></div>` : ''}
                 ${b.trip.duration ? `<div><dt>Duration</dt><dd>${esc(b.trip.duration)}</dd></div>` : ''}`}
            ${b.trip.variant && b.trip.variant !== 'Default Title' ? `<div><dt>Package</dt><dd>${esc(b.trip.variant)}</dd></div>` : ''}
          </dl>
        </section>
        <section class="clpt-card">
          <h3 class="clpt-card__title">Primary booker</h3>
          <dl class="clpt-rows">
            <div><dt>Name</dt><dd>${esc(b.booker.name)}</dd></div>
            <div><dt>Email</dt><dd>${esc(b.booker.email)}</dd></div>
            <div><dt>Mobile</dt><dd>${esc(b.booker.phone)}</dd></div>
            <div><dt>City</dt><dd>${esc(b.booker.city)}</dd></div>
          </dl>
        </section>
        <section class="clpt-card">
          <h3 class="clpt-card__title">${w.People} (${b.seats})</h3>
          <ol class="clpt-tlist">${b.travellers.map((t, i) => `<li><span>${i + 1}. ${esc(t.name)}</span><span>${esc(t.age)} yrs</span></li>`).join('')}</ol>
        </section>
        <section class="clpt-card">
          <h3 class="clpt-card__title">Payment</h3>
          <dl class="clpt-rows">
            <div><dt>Total ${w.what} amount</dt><dd>${money(b.total_amount, cur)}</dd></div>
            ${b.balance_due > 0 ? `<div><dt>Balance due</dt><dd>${money(b.balance_due, cur)}</dd></div>` : ''}
            <div><dt>Payment ID</dt><dd>${esc(b.payment_id)}</dd></div>
            <div class="is-total"><dt>Amount paid</dt><dd>${money(b.amount_paid, cur)}</dd></div>
          </dl>
        </section>`;
      const wa = this.$('[data-clpt-wa]');
      if (wa && this.cfg.whatsapp) {
        const msg = `${this.cfg.whatsapp_message || 'Hi Club Laparoz! My booking reference is'} ${b.booking_ref} (${b.trip.title}).`;
        wa.href = `https://wa.me/${this.cfg.whatsapp}?text=${encodeURIComponent(msg)}`;
      }
    }

    downloadBooking() {
      const b = this.state.confirmed;
      if (!b) return;
      const bytes = buildBookingPdf(b, this.cfg);
      const href = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = href;
      a.download = `club-laparoz-booking-${b.booking_ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
      this.toast('Booking details PDF downloaded');
    }
  }

  customElements.define('club-laparoz-trip', ClubLaparozTrip);
})();
